<script>
import Map from '@/components(new)/UI/Maps/SustainablePracticesMap/SustainablePracticesMap.vue'
import TitleWithRow from '@/components(new)/UI/Typography/TitleWithRow/TitleWithRow.vue'
import AppNotificacao from '@/components/mixins/AppNotificacao'
import * as service from '@/services/georreferenciamento'
import md5 from 'md5'

export default {
  mixins: [
    AppNotificacao
  ],
  components: {
    TitleWithRow,
    Map
  },
  props: {
    protocolo: {
      type: String,
      required: true
    },
    cadastro: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      spsList: [],
      propertyShapeImovel: null,
      vectorizedAreas: {},
      info: {
        title: 'Atenção',
        message: 'Para realizar o georreferenciamento das práticas, é necessário primeiro incluir a geometria do imóvel na aba correspondente e cadastrar ao menos uma prática sustentável.'
      }
    }
  },
  methods: {
    validate () {
      const isValid = this.spsList.length === Object.values(this.vectorizedAreas).length
      if (!isValid) {
        this.showNotificacao(
          this.$messages.TIPO_ERROR,
          this.$messages.TITULO_ERROR,
          this.$messages.VETORIZAR_AREAS_COM_SP
        )
        throw new Error()
      }
      return isValid
    },
    async loadGeorreferenciamentoData () {
      if (!this.hasAnyPraticaAndGeoJsonImovel) return

      this.cleanMapLayers()

      const { praticas, imovel } = await service.getGeorreferenciamentoData(this.protocolo)
      this.spsList = praticas
      this.propertyShapeImovel = imovel

      this.syncMapWithSpsList()
    },
    async submit () {
      await service.saveGeorreferenciamento(this.protocolo, this.vectorizedAreas)
    },
    updateVectorizedAreas (evt) {
      this.vectorizedAreas = evt
    },
    renderPratices () {
      this.spsList = [...this.spsList]
      this.syncMapWithSpsList()
    },
    onPraticaRemovida (codigoPratica) {
      this.spsList = this.spsList.filter(p => p.codigo !== codigoPratica)
      if (this.vectorizedAreas[codigoPratica]) {
        delete this.vectorizedAreas[codigoPratica]
      }
      this.removePraticaFromMap(codigoPratica)

      if (this.$refs.mapComponent?.removePraticaFromVectorizedSps) {
        this.$refs.mapComponent.removePraticaFromVectorizedSps(codigoPratica)
      }
    },
    removeLayersByCondition (condition) {
      const mapComponent = this.$refs.mapComponent
      if (!mapComponent?.mapManager?.map) return

      const map = mapComponent.mapManager.map
      const layersToRemove = []
      map.eachLayer(layer => {
        if (condition(layer)) {
          layersToRemove.push(layer)
        }
      })
      layersToRemove.forEach(layer => {
        map.removeLayer(layer)
      })
    },
    removePraticaFromMap (codigoPratica) {
      const pratica = this.spsList.find(p => p.codigo === codigoPratica)
      if (!pratica) return
      this.removeLayersByCondition(layer => layer.options?.name === pratica.name)
    },
    cleanMapLayers () {
      this.removeLayersByCondition(layer => layer.options?.codigo || layer.options?.id)
    },
    syncMapWithSpsList () {
      this.removeLayersByCondition(layer => {
        const layerName = layer.options?.name
        return layerName && layerName !== 'property' && !this.spsList.find(p => p.name === layerName)
      })
    }
  },
  computed: {
    hasAnyPraticaAndGeoJsonImovel () {
      const praticas = this.cadastro.praticas
      const hasAnyPratica = Object.values(praticas).some(value => Array.isArray(value) ? value.length > 0 : !!value)
      const hasGeoJsonImovel = Array.isArray(this.cadastro.imovel.geoJson) && this.cadastro.imovel.geoJson.length
      return !!(hasGeoJsonImovel && hasAnyPratica)
    },
    mapKey () {
      if (!this.propertyShapeImovel) return 'shape-pending'
      // Usa o md5 para deixar a chave mais sensível a ordenação dos valores e etc, para que detecte a mudança de geometria
      // mesmo em caso de geometrias parecidas
      return md5(JSON.stringify(this.propertyShapeImovel))
    }
  },

  activated () {
    this.loadGeorreferenciamentoData()
  },
  expose: ['validate', 'submit', 'onPraticaRemovida']
}
</script>

<template>
  <div v-if="!hasAnyPraticaAndGeoJsonImovel" class="info">
    <el-card style="width: 500px" class="m-2">
      <div style="display: flex; justify-content: center;">
        <h2 class="titulo">
          <i class="far fa-exclamation-triangle" style="color: orange;"></i>&nbsp;<b>{{ info.title }}</b>
        </h2>
      </div>
      <hr>
      <div>
        <p>{{ info.message }}</p>
      </div>
    </el-card>
  </div>
  <validation-observer v-else ref="localObserver" tag="div" class="sedap-georreferenciamento-form">
    <TitleWithRow class="sedap-georreferenciamento-form__title" title="Georreferenciamento das práticas realizadas na área" />
    <div class="sedap-georreferenciamento-form__instructions">
      <p>Faça o georreferenciamento de cada prática dentro dos limites do seu imóvel, desenhando ou fazendo o upload da área correspondente.</p>
      <div class="sedap-georreferenciamento-form__instructions__steps">
        <p>Siga os seguintes passos:</p>
        <div class="sedap-georreferenciamento-form__instructions__steps-row">
          <p>1. Selecione a prática</p>
          <p>Clique no botão de edição (ícone de lápis) ao lado da prática desejada na lista (ex: Recuperação de Pastagens Degradadas - PRPD).</p>
        </div>
        <div class="sedap-georreferenciamento-form__instructions__steps-row">
          <p>2. Faça o georreferenciamento</p>
          <p>Clique no botão "Desenhar polígono" para desenhar a área diretamente no mapa ou clique no botão "Importar shape" para fazer o upload do seu arquivo vetorial (shapefile ou .kml).</p>
        </div>
        <div class="sedap-georreferenciamento-form__instructions__steps-row">
          <p>3. Finalize o georreferenciamento</p>
          <p>Se desenhou a área ou fez upload do arquivo, clique em "Finalizar edição" e depois em "Salvar" para concluir.</p>
        </div>
      </div>
    </div>
    <Map
      ref="mapComponent"
      v-if="spsList.length"
      :pratices="spsList"
      :propertyShapeImovel="propertyShapeImovel"
      :key="mapKey"
      @updateAreas="updateVectorizedAreas"
      @renderPratices="renderPratices"
    />
  </validation-observer>
</template>

<style lang="scss">
.titulo {
  margin: 0;
}
@import './styles.scss'
</style>



// WEBPACK FOOTER //
// src/App/(authenticated)/modulos/cadastro/formulario-cadastro/georreferenciamento/georreferenciamento.vue